import { collection } from 'infra/collection'
import { Id } from 'types/definitions'
import { IAMAPI } from './API'
import { UpdateUserDataPayload, UserDataDTO } from './dtos/user-data'
import memoize from 'memoizee'
import UserData from './models/UserData'

/**
 * Represents a function that retrieves user data by their public ID.
 *
 * @function
 * @param {Id} publicId - The public ID of the user.
 * @returns {UserData | null} The user data if found, otherwise null.
 */
export type GetUserData = (publicId: Id) => UserData | null

/**
 * Represents a function that fetches user data by their public ID.
 */
export type FetchUserData = (id: Id) => Promise<UserData>

export default class UserDataStore {
  private readonly userData = collection<UserDataDTO, UserData>({
    idAttribute: 'user_public_id',
    create: () => new UserData(),
  })

  constructor(private api: IAMAPI) {
    this.getByPublicId = this.getByPublicId.bind(this)
  }

  /**
   * Gets already-fetched user data for the given user.
   *
   * @param publicId
   */
  getByPublicId(publicId: Id): UserData | null {
    return this.userData.get(publicId) ?? null
  }

  /**
   * Fetches user data by public ID.
   *
   * @param publicId
   */
  private readonly _fetchByPublicId = memoize(
    async (publicId: Id) =>
      this.api.userData.getByPublicId(publicId).then(this.userData.set),
    {
      length: 1,
      promise: true,
    },
  )

  /**
   * Fetches user data by public ID.
   *
   * @param publicId
   * @param skipCache
   */
  fetchByPublicId = async (publicId: Id, skipCache = false) => {
    if (skipCache) {
      await this._fetchByPublicId.delete(publicId)
    }
    return this._fetchByPublicId(publicId)
  }

  async update(publicId: Id, payload: UpdateUserDataPayload) {
    await this.api.userData.update(publicId, payload)

    this.fetchByPublicId(publicId, true).then()
  }
}
